/**
 * Home's pure parts (design 17, D-088, D-089): the "My jobs" query and its paging, and the note
 * stream's display names. No DOM, no network — tested alone.
 */

import type { JobListRow, NoteStreamItem } from '@unitedos/shared';

/** A page of My jobs: Jobber's Active tab, the caller's own (`mine=1`, D-089), newest number first. */
export const MY_JOBS_PAGE_SIZE = 25;

export function myJobsQuery(page: number): URLSearchParams {
  return new URLSearchParams({ status: 'active', mine: '1', page: String(page), size: String(MY_JOBS_PAGE_SIZE) });
}

export interface MyJobsPages {
  readonly rows: readonly JobListRow[];
  /** Jobber's count of the caller's active jobs, once the first page has answered. */
  readonly total: number | null;
  /** The next page to ask for; null once every row is here. */
  readonly next: number | null;
}

export const NO_PAGES: MyJobsPages = { rows: [], total: null, next: 1 };

/** The next page appended; done when the rows reach the total or a page comes back short. */
export function appendPage(pages: MyJobsPages, page: number, rows: readonly JobListRow[], total: number): MyJobsPages {
  const seen = new Set(pages.rows.map((r) => r.jobNumber));
  const all = [...pages.rows, ...rows.filter((r) => !seen.has(r.jobNumber))];
  const done = all.length >= total || rows.length < MY_JOBS_PAGE_SIZE;
  return { rows: all, total, next: done ? null : page + 1 };
}

/**
 * A note's text for a clamped card: runs of blank lines collapsed to one break and the ends trimmed,
 * so the three visible lines all carry words (intake notes separate their fields with blank lines).
 */
export function noteExcerpt(message: string | null): string {
  return (message ?? '').replace(/\r\n?/g, '\n').replace(/[ \t]*\n(?:[ \t]*\n)+/g, '\n').trim();
}

/** Who wrote a note: the Jobber user, or "Jobber" for a note the system made (no name, another type). */
export function noteAuthor(note: Pick<NoteStreamItem, 'createdByName' | 'createdByType'>): string {
  const name = note.createdByName?.trim();
  if (name) return name;
  return note.createdByType && note.createdByType !== 'User' ? 'Jobber' : 'Unknown';
}

/** Where a note's card opens: the job's General tab, where the notes panel is; nowhere without a number. */
export function noteHref(note: Pick<NoteStreamItem, 'jobNumber'>): string | null {
  return note.jobNumber === null ? null : `/jobs/${note.jobNumber}/general`;
}
